import { Directive, ElementRef, Input, OnInit, Renderer2 } from '@angular/core';
import { OnChange } from '@df/ng/on-change';

export const DEFAULT_GRID_COLUMNS = 12;

/**
 * Change element to grid.
 *
 * Default `gridColumnGap` is `4 * var(--grid-gutter-size, 5px)`
 *
 * @example Simple grid
 * ```html
 * <div grid="4">
 *   <div gridColumn="1"></div>
 *   <div gridColumn="2"></div>
 *   <div gridColumn="1"></div>
 * </div>
 *
 * // or
 * <div grid gridColumns="4">
 *   <div></div> <!-- default column span is 1 and default child element is grid column -->
 *   <div gridColumn gridColumnSpan="2"></div>
 *   <div></div>
 * </div>
 * ```
 *
 * @example Grid without gap
 * <div grid="4" gridColumnGap="0">
 *   <div gridColumn="1"></div>
 *   <div gridColumn="2"></div>
 *   <div gridColumn="1"></div>
 * </div>
 */

type TStylePropertyVariable = {
    value: any;
    variable: string;
};

type TStylePropertyStyle = {
    value: any;
    style: string;
};

type TStyleProperty = TStylePropertyVariable | TStylePropertyStyle;

type TStylePropertyId =
    | 'styleCpGridColumns'
    | 'styleCpGridColumnSpan'
    | 'styleCpGridColumnOffset'
    | 'styleCpGridRowGap'
    | 'styleCpGridColumnGap'
    | 'styleCpGridAutoFlow'
    | 'styleDisplay'
    | 'styleWidth'
    | 'styleGridTemplateColumns'
    | 'styleRowGap'
    | 'styleColumnGap'
    | 'styleBottomMargin'
    | 'styleGridAutoFlow';

type TStyleProperties = { [key in TStylePropertyId]: TStyleProperty };

@Directive({
    selector: 'grid, [grid]',
    standalone: true
})
export class GridDirective implements OnInit {
    private styleProperties: TStyleProperties = {
        styleCpGridColumns: {
            value: 12,
            variable: '--grid-columns'
        },
        styleCpGridColumnSpan: {
            value: 1,
            variable: '--grid-column-span'
        },
        styleCpGridColumnOffset: {
            value: 1,
            variable: '--grid-column-offset'
        },
        styleCpGridRowGap: {
            value: `calc(var(--grid-gutter-size, 5px) * 0)`,
            variable: '--grid-row-gap'
        },
        styleCpGridColumnGap: {
            value: `calc(var(--grid-gutter-size, 5px) * 0)`,
            variable: '--grid-column-gap'
        },
        styleCpGridAutoFlow: {
            value: `row dense`,
            variable: '--grid-auto-flow'
        },
        styleDisplay: {
            value: 'grid',
            style: 'display'
        },
        styleWidth: {
            value: '100%',
            style: 'width'
        },
        styleGridTemplateColumns: {
            value: '[column-start] repeat(var(--grid-columns, 12), 1fr) [column-end]',
            style: 'grid-template-columns'
        },
        styleRowGap: {
            value: 'var(--grid-row-gap, 0)',
            style: 'row-gap'
        },
        styleColumnGap: {
            value: 'var(--grid-column-gap, 0)',
            style: 'column-gap'
        },
        styleBottomMargin: {
            value: undefined,
            style: 'margin-bottom'
        },
        styleGridAutoFlow: {
            value: 'var(--grid-auto-flow, row dense)',
            style: 'grid-auto-flow'
        }
    };

    /** Grid columns number */
    @Input('grid')
    @OnChange<number>(function (this: GridDirective, value: number) {
        const that = this as GridDirective;
        that.gridColumns = value || DEFAULT_GRID_COLUMNS;
        that.updateColumns();
        that.updateBottomMargin();
    })
    grid?: number;

    /** Grid columns number - alias */
    @Input('gridColumns')
    @OnChange<number>('update')
    gridColumns: number = DEFAULT_GRID_COLUMNS;

    /** Grid columns number - alias */
    @Input('gridColumnsInRow')
    @OnChange<number>(function (this: GridDirective, value?: number) {
        const that = this as GridDirective;
        that.gridColumns = value || DEFAULT_GRID_COLUMNS;
        that.updateColumns();
    })
    gridColumnsInRow?: number;

    /** Auto-placement algorithm */
    @Input('gridAutoFlow')
    @OnChange<number>('updateAutoFlow')
    gridAutoFlow: 'column' | 'column dense' | 'row' | 'row dense' = 'row dense';

    /** Grid columns gap */
    @Input('gridColumnGap')
    @OnChange<number>('updateColumnGap')
    gridColumnGap: number | string = 4;

    /** Grid row gap */
    @Input('gridRowGap')
    @OnChange<number>('updateRowGap')
    gridRowGap: number | string = 4;

    /**
     * Number of rows that are empty and we need to cut their gutters off
     * In practive these rows can be hidden
     */
    @Input()
    @OnChange<number>('updateBottomMargin')
    gridOverflowRows = 0;

    @Input()
    test?: boolean;

    private initiated = false;

    constructor(
        private elementRef: ElementRef,
        private renderer2: Renderer2
    ) {}

    ngOnInit() {
        this.update();
        this.initiated = true;
        for (const key in this.styleProperties) {
            this.applyStyleProperty(key as TStylePropertyId);
        }
    }

    get element(): HTMLElement {
        return this.elementRef.nativeElement;
    }

    update() {
        this.updateRowGap();
        this.updateColumns();
        this.updateColumnGap();
        this.updateAutoFlow();
    }

    private updateColumns() {
        this.setStyleProperty('styleCpGridColumns', this.gridColumnsInRow || this.gridColumns || 12);
    }

    private updateRowGap() {
        this.setStyleProperty(
            'styleCpGridRowGap',
            this.gridRowGap
                ? typeof this.gridRowGap === 'string'
                    ? this.gridRowGap
                    : `calc(var(--grid-gutter-size, 5px) * ${this.gridRowGap || 0})`
                : undefined
        );
        this.updateBottomMargin();
    }

    private updateColumnGap() {
        this.setStyleProperty(
            'styleCpGridColumnGap',
            this.gridColumnGap
                ? typeof this.gridColumnGap === 'string'
                    ? this.gridColumnGap
                    : `calc(var(--grid-gutter-size, 5px) * ${this.gridColumnGap || 0})`
                : undefined
        );
    }

    private updateBottomMargin() {
        this.setStyleProperty(
            'styleBottomMargin',
            this.gridOverflowRows && this.gridRowGap !== undefined
                ? typeof this.gridRowGap === 'string'
                    ? `calc( -${this.gridOverflowRows} * ${this.gridRowGap} )`
                    : `calc( -${this.gridOverflowRows} * var(--grid-gutter-size, 5px) * ${this.gridRowGap || 0})`
                : undefined
        );
    }

    private updateAutoFlow() {
        this.setStyleProperty('styleCpGridAutoFlow', `${this.gridAutoFlow || 'row dense'}`);
    }

    private setStyleProperty(key: TStylePropertyId, value: any) {
        const styleProperty = this.styleProperties[key];
        if (styleProperty.value !== value) {
            styleProperty.value = value;
            this.applyStyleProperty(key);
        }
    }

    /**
     * Note that hostBinding it too slow and fires after content renderns so we need to use renderer2
     */
    private applyStyleProperty(key: TStylePropertyId) {
        if (this.initiated) {
            const property = this.styleProperties[key];
            if ('variable' in property) {
                property.value === undefined
                    ? this.element.style.removeProperty(property.variable)
                    : this.element.style.setProperty(property.variable, property.value.toString());
            } else {
                property.value === undefined
                    ? this.renderer2.removeStyle(this.element, property.style)
                    : this.renderer2.setStyle(this.element, property.style, property.value.toString());
            }
        }
    }
}
